/**
 * Custom React Hook for fetching alerts and thresholds
 * Includes loading states, error handling, and acknowledge action
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchAlerts,
  fetchThresholds,
  acknowledgeAlert,
} from '@/api/alertsApi';
import type { AlertItem, AlertThresholdItem } from '@/api/alertsApi';

interface UseAlertsResult {
  alerts: AlertItem[];
  thresholds: AlertThresholdItem[];
  loading: boolean;
  error: string | null;
  activeCount: number;
  refresh: () => Promise<void>;
  acknowledge: (alertId: string) => Promise<void>;
}

export function useAlerts(): UseAlertsResult {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [thresholds, setThresholds] = useState<AlertThresholdItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [alertsData, thresholdsData] = await Promise.all([
        fetchAlerts(),
        fetchThresholds(),
      ]);
      setAlerts(alertsData);
      setThresholds(thresholdsData);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch alerts';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const acknowledge = useCallback(async (alertId: string) => {
    await acknowledgeAlert(alertId);
    setAlerts((prev) =>
      prev.map((a) =>
        a.id === alertId
          ? { ...a, status: 'acknowledged' as const, acknowledgedAt: new Date().toISOString() }
          : a
      )
    );
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeCount = alerts.filter((a) => a.status === 'active').length;

  return { alerts, thresholds, loading, error, activeCount, refresh, acknowledge };
}
